import type { AbstractPowerSyncDatabase } from "@powersync/web";

// Ask Propel, on-device edition (core-features §5). Answers come from the field
// notes synced to this device: visit summaries, voice-note transcripts, notes,
// photo text (OCR), signed reports and form metrics. Every statement carries a
// citation back to the exact visit or observation it came from. Nothing leaves
// the device. The server's AI Ask Propel plugs in behind the same interface
// once the web API exists (docs/decisions/0002).

export interface Citation { n: number; kind: "summary" | "voice" | "note" | "photo" | "report" | "metric"; visitId: string; obsId?: string; location: string; date: string; excerpt: string }
export interface Answer { headline: string; points: { text: string; cites: number[] }[]; trend?: { metric: string; from: number; to: number; unit: string; n: number; cite: number }; citations: Citation[]; scope: { label: string; days: number | null; visits: number } }

const STOP = new Set("a an and are as at be been by did do does for from gone had has have how i in is it its of on or over past so that the their there this to was were what when where which who why will with has any last recent recently".split(" "));
const SYN: Record<string, string[]> = {
  stand: ["stand", "establishment", "emergence", "germination", "population", "count"],
  establishment: ["stand", "establishment", "emergence", "germination"],
  pest: ["pest", "aphid", "aphids", "insect", "leafhopper", "hopperburn", "borer"],
  disease: ["disease", "blight", "leaf spot", "lesion", "lesions", "frogeye", "damping", "mold", "rot"],
  weed: ["weed", "weeds", "waterhemp", "escapes"],
  water: ["water", "moisture", "wet", "irrigation", "tile", "drain", "rain"],
  nutrient: ["nitrogen", "nutrient", "deficiency", "sidedress", "ec", "ph", "tissue"],
  yield: ["yield", "brix", "fruit", "set", "cutting", "stems", "bushel"],
  followup: ["follow", "follow-up", "remind", "check", "next", "task", "ask"],
};
const words = (s: string) => s.toLowerCase().replace(/[^a-z0-9%.\s-]/g, " ").split(/\s+/).filter((w) => w && !STOP.has(w));
const stem = (w: string) => w.replace(/(ing|ed|es|s)$/, "");

export function parseRange(q: string): number | null {
  const t = q.toLowerCase();
  const n = (w: string) => ({ one: 1, two: 2, three: 3, four: 4, five: 5, six: 6 } as Record<string, number>)[w] ?? parseInt(w, 10);
  let m = t.match(/(?:past|last)\s+(\w+)\s+(day|week|month)s?/);
  if (m) return n(m[1]) * ({ day: 1, week: 7, month: 30 } as Record<string, number>)[m[2]];
  m = t.match(/(?:past|last|this)\s+(day|week|month|season|year)/);
  if (m) return ({ day: 1, week: 7, month: 30, season: 150, year: 365 } as Record<string, number>)[m[1]];
  return null;
}
const lastN = (q: string) => { const m = q.toLowerCase().match(/last\s+(\d+|one|two|three|four|five)\s+visits?/); return m ? ({ one: 1, two: 2, three: 3, four: 4, five: 5 } as Record<string, number>)[m[1]] ?? parseInt(m[1], 10) : null; };

interface Doc { kind: Citation["kind"]; visitId: string; obsId?: string; location: string; date: string; text: string }

export async function askLocal(db: AbstractPowerSyncDatabase, teamId: string, question: string, locationId?: string | null): Promise<Answer> {
  const days = parseRange(question);
  const n = lastN(question);
  const scopeIds: string[] = locationId
    ? [locationId, ...(await db.getAll<{ id: string }>(`WITH RECURSIVE d(id, depth) AS (SELECT member_id, 1 FROM mobile_zone_memberships WHERE zone_id = ? UNION SELECT z.member_id, d.depth + 1 FROM mobile_zone_memberships z JOIN d ON z.zone_id = d.id WHERE d.depth < 6) SELECT id FROM d`, [locationId])).map((r) => r.id)]
    : [];
  const scopeLabel = locationId ? (await db.getOptional<{ label: string }>("SELECT label FROM map_features WHERE id = ?", [locationId]))?.label ?? "that location" : "all locations";
  const since = days ? new Date(Date.now() - days * 86_400_000).toISOString() : "1970";
  const inScope = scopeIds.length ? `AND fn.map_feature_id IN (${scopeIds.map(() => "?").join(",")})` : "";
  let visits = await db.getAll<{ id: string; ai_summary: string | null; started_at: string; label: string }>(
    `SELECT fn.id, fn.ai_summary, fn.started_at, mf.label FROM mobile_field_notes fn JOIN map_features mf ON mf.id = fn.map_feature_id JOIN maps m ON m.id = mf.map_id
     WHERE m.team_id = ? AND coalesce(fn.started_at, fn.created_at) >= ? ${inScope} ORDER BY fn.started_at DESC LIMIT 200`, [teamId, since, ...scopeIds]);
  if (n) visits = visits.slice(0, n);
  const ids = visits.map((v) => v.id);
  const byId = new Map(visits.map((v) => [v.id, v]));
  const docs: Doc[] = [];
  for (const v of visits) if (v.ai_summary) docs.push({ kind: "summary", visitId: v.id, location: v.label, date: v.started_at, text: v.ai_summary });
  if (ids.length) {
    const ph = ids.map(() => "?").join(",");
    const obs = await db.getAll<{ id: string; field_note_id: string; content_type: string | null; body: string | null; raw_transcript: string | null; image_text_ai: string | null; created_at: string }>(`SELECT id, field_note_id, content_type, body, raw_transcript, image_text_ai, created_at FROM mobile_field_note_media WHERE field_note_id IN (${ph})`, ids);
    for (const o of obs) {
      const v = byId.get(o.field_note_id)!;
      if (o.raw_transcript) docs.push({ kind: "voice", visitId: v.id, obsId: o.id, location: v.label, date: o.created_at, text: o.raw_transcript });
      if (o.body) docs.push({ kind: "note", visitId: v.id, obsId: o.id, location: v.label, date: o.created_at, text: o.body });
      if (o.image_text_ai && (o.content_type || "").startsWith("image/")) docs.push({ kind: "photo", visitId: v.id, obsId: o.id, location: v.label, date: o.created_at, text: o.image_text_ai });
    }
    const reps = await db.getAll<{ field_note_id: string; report_data: string | null; generated_at: string | null }>(`SELECT field_note_id, report_data, generated_at FROM mobile_custom_report_statuses WHERE field_note_id IN (${ph})`, ids);
    for (const r of reps) {
      try {
        const data = JSON.parse(r.report_data || "{}");
        const recs = (data.sections || []).filter((s: any) => /recommend|next|action|follow/i.test(s.heading || ""));
        for (const s of recs) {
          const t = Array.isArray(s.content) ? s.content.join(". ") : String(s.content ?? "");
          const v = byId.get(r.field_note_id)!;
          if (t) docs.push({ kind: "report", visitId: v.id, location: v.label, date: r.generated_at || v.started_at, text: `${s.heading}: ${t}` });
        }
      } catch { /* skip unparseable */ }
    }
  }

  // Score sentences by expanded keyword overlap, lightly favoring recent ones.
  const qTerms = new Set(words(question).flatMap((w) => [w, ...(SYN[stem(w)] ?? SYN[w] ?? [])]).map(stem));
  const broad = qTerms.size === 0 || /summar|overview|what happened|how.*(going|gone)|update/i.test(question);
  type Hit = { doc: Doc; sentence: string; score: number };
  const hits: Hit[] = [];
  for (const d of docs) {
    for (const sentence of d.text.split(/(?<=[.!?])\s+/).map((s) => s.trim()).filter((s) => s.length > 12)) {
      const ws = words(sentence).map(stem);
      let score = ws.filter((w) => qTerms.has(w)).length;
      if (broad && d.kind === "summary") score += 1.5;
      if (d.kind === "report") score += 0.3;
      const age = (Date.now() - new Date(d.date).getTime()) / 86_400_000;
      score += Math.max(0, 1 - age / 60) * 0.5;
      if (score >= (broad ? 1.2 : 1)) hits.push({ doc: d, sentence, score });
    }
  }
  hits.sort((a, b) => b.score - a.score);
  const picked: Hit[] = [];
  for (const h of hits) {
    const k = words(h.sentence).slice(0, 6).join(" ");
    if (picked.some((p) => words(p.sentence).slice(0, 6).join(" ") === k)) continue;
    picked.push(h);
    if (picked.length >= 6) break;
  }
  picked.sort((a, b) => new Date(a.doc.date).getTime() - new Date(b.doc.date).getTime());

  const citations: Citation[] = [];
  const cite = (d: Doc, excerpt: string) => {
    const existing = citations.find((c) => c.visitId === d.visitId && c.obsId === d.obsId && c.kind === d.kind);
    if (existing) return existing.n;
    citations.push({ n: citations.length + 1, kind: d.kind, visitId: d.visitId, obsId: d.obsId, location: d.location, date: d.date, excerpt });
    return citations.length;
  };
  const points = picked.map((h) => ({ text: h.sentence, cites: [cite(h.doc, h.sentence)] }));

  // Numeric trend when the question names a tracked metric.
  let trend: Answer["trend"];
  if (ids.length) {
    const types = await db.getAll<{ id: string; name: string }>("SELECT id, name FROM metric_types WHERE team_id = ?", [teamId]);
    const mt = types.find((t) => words(t.name).map(stem).some((w) => qTerms.has(w)));
    if (mt) {
      const rows = await db.getAll<{ value: string; created_at: string; field_note_id: string }>(`SELECT value, created_at, field_note_id FROM mobile_metrics WHERE metric_type_id = ? AND field_note_id IN (${ids.map(() => "?").join(",")}) ORDER BY created_at`, [mt.id, ...ids]);
      const vals = rows.map((r) => ({ v: parseFloat(r.value), r })).filter((x) => !Number.isNaN(x.v));
      if (vals.length >= 2) {
        const last = vals[vals.length - 1];
        const v = byId.get(last.r.field_note_id)!;
        const unit = mt.name.match(/\(([^)]+)\)/)?.[1] ?? "";
        trend = { metric: mt.name.replace(/\s*\(.*\)/, ""), from: vals[0].v, to: last.v, unit, n: vals.length, cite: cite({ kind: "metric", visitId: v.id, location: v.label, date: last.r.created_at, text: "" }, `${mt.name}: ${last.v}`) };
      }
    }
  }

  const when = n ? `the last ${Math.min(n, visits.length)} visits` : days ? `the past ${days >= 30 ? `${Math.round(days / 30)} month${days >= 60 ? "s" : ""}` : days % 7 === 0 ? `${days / 7} week${days > 7 ? "s" : ""}` : `${days} days`}` : "all synced visits";
  const headline = !visits.length
    ? `No visits to ${scopeLabel} in ${when}.`
    : !points.length
      ? `${visits.length} ${visits.length === 1 ? "visit" : "visits"} to ${scopeLabel} in ${when}, but none of the notes mention that.`
      : `From ${visits.length} ${visits.length === 1 ? "visit" : "visits"} to ${scopeLabel} in ${when}:`;
  return { headline, points, trend, citations, scope: { label: scopeLabel, days, visits: visits.length } };
}
